import { useEffect, useLayoutEffect, type RefObject } from "react";
import { crewIdFromPathname, crewSurfaceFromPathname } from "../lib/routes";
import { browserSessionStorage, crewScroll, rememberCrewScroll, rememberCrewSurface } from "../lib/crewContinuity";

const unfoldedQuery = "(min-width: 45rem)";

export function useCrewContinuity(
  sessionId: string,
  pathname: string,
  detailPaneRef: RefObject<HTMLElement>,
) {
  const crewId = crewIdFromPathname(pathname);
  const surface = crewSurfaceFromPathname(pathname);

  useEffect(() => {
    if (!crewId || !surface) return;
    rememberCrewSurface(browserSessionStorage(), sessionId, crewId, surface);
  }, [crewId, sessionId, surface]);

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    if (!crewId || !surface) return;
    const media = window.matchMedia(unfoldedQuery);
    const storage = browserSessionStorage();
    let unfolded = media.matches;
    let saveFrame = 0;
    let restoreFrame = 0;
    let listening = false;
    let hasRestored = false;
    let lastKnownOffset = crewScroll(storage, sessionId, crewId, surface);

    const readOffset = () => unfolded
      ? detailPaneRef.current?.scrollTop ?? 0
      : window.scrollY;

    const saveNow = () => {
      if (saveFrame) {
        window.cancelAnimationFrame(saveFrame);
        saveFrame = 0;
      }
      if (hasRestored) rememberCrewScroll(storage, sessionId, crewId, surface, lastKnownOffset);
    };

    const scheduleSave = () => {
      lastKnownOffset = readOffset();
      if (saveFrame) return;
      saveFrame = window.requestAnimationFrame(() => {
        saveFrame = 0;
        saveNow();
      });
    };

    const removeScrollListener = () => {
      if (!listening) return;
      if (unfolded) detailPaneRef.current?.removeEventListener("scroll", scheduleSave);
      else window.removeEventListener("scroll", scheduleSave);
      listening = false;
    };

    const addScrollListener = () => {
      if (listening) return;
      if (unfolded) detailPaneRef.current?.addEventListener("scroll", scheduleSave, { passive: true });
      else window.addEventListener("scroll", scheduleSave, { passive: true });
      listening = true;
    };

    const restore = () => {
      const offset = crewScroll(storage, sessionId, crewId, surface);
      lastKnownOffset = offset;
      if (unfolded) {
        if (detailPaneRef.current) detailPaneRef.current.scrollTop = offset;
      } else {
        window.scrollTo({ top: offset, left: 0, behavior: "auto" });
      }
      hasRestored = true;
    };

    const scheduleRestore = () => {
      if (restoreFrame) window.cancelAnimationFrame(restoreFrame);
      restoreFrame = window.requestAnimationFrame(() => {
        restoreFrame = 0;
        restore();
        addScrollListener();
      });
    };

    const handlePostureChange = (event: MediaQueryListEvent) => {
      saveNow();
      removeScrollListener();
      unfolded = event.matches;
      hasRestored = false;
      scheduleRestore();
    };

    scheduleRestore();
    media.addEventListener("change", handlePostureChange);
    window.addEventListener("pagehide", saveNow);

    return () => {
      if (restoreFrame) window.cancelAnimationFrame(restoreFrame);
      saveNow();
      removeScrollListener();
      media.removeEventListener("change", handlePostureChange);
      window.removeEventListener("pagehide", saveNow);
    };
  }, [crewId, detailPaneRef, sessionId, surface]);
}
